import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import {
  canViewLocalOpsConcierge,
  LOCALOPS_CONCIERGE_ROUTE,
  LOCALOPS_OFFERING_CONCIERGE_ROUTE,
} from '../../utils/localOpsOpsAccess';
import {
  applyPatternToForm,
  buildDefaultConciergeForm,
  buildDraftPayload,
  CONCIERGE_PATTERNS,
  getDynamicLabels,
  getPriceSuggestion,
  getRelevantScriptTitles,
  getUseCasePreset,
  SCRIPT_CARDS,
  SOURCE_OPTIONS,
  trim,
  USE_CASE_OPTIONS,
} from '../../utils/localOpsConciergeIntelligence';
import {
  calculateConciergeRouteQuote,
  getConfirmedSeededCandidate,
  resolveConciergePlaceCandidates,
} from '../../utils/localOpsConciergePlaceResolver';
import './LocalOpsConcierge.css';

const TOOLTIP_COPY = {
  requesterEmail:
    'Used to send the secure review link. The requester is not committed until they review and fund the task.',
  source:
    'Where this need came from. Helps track what channels create funded tasks.',
  sourceLabel:
    'Short label like Hip Camden DM, Down Home call, or phone call.',
  useCase:
    'Drives safe draft defaults for category, task type, task copy, and suggested next scripts.',
  suggestedPriceUsd:
    'The requester can review before funding. Helpers are notified only after the task is funded.',
  requesterFacingNotes:
    'Shown to the requester to help them review the task. Do not put private operator notes here.',
  internalNotes:
    'Only for the concierge/operator. Never sent to requester, helper, public task pages, or share pages.',
  originalRequest:
    'Operator-only capture of the source post, message, phone note, or restaurant note. It is not submitted unless you copy it into task fields.',
};

const OPTIONAL_LABEL = 'Optional';
const EMPTY_RESOLUTION = Object.freeze({
  candidates: [],
  status: 'idle',
  message: '',
});
const PATTERN_OWNED_MANUAL_FIELDS = [
  'title',
  'description',
  'requesterFacingNotes',
  'internalNotes',
  'pickupAddress',
  'category',
  'suggestedPriceUsd',
];
const DEFAULT_ROUTE_PRICING_RULE = {
  type: 'per_mile',
  rate: 2,
  round: 'ceil_dollar',
};

const normalizeRows = (payload) => {
  const data = payload?.data?.data || payload?.data || payload || [];
  return Array.isArray(data) ? data : [];
};

const candidateDisplayText = (candidate) =>
  trim(candidate?.addressText || candidate?.publicAddress || candidate?.name);

const candidateMatchesText = (candidate, value) => {
  const text = trim(value).toLowerCase();
  if (!candidate || !text) return false;
  return [
    candidate.addressText,
    candidate.publicAddress,
    candidate.name,
    candidate.label,
  ]
    .map((item) => trim(item).toLowerCase())
    .filter(Boolean)
    .some((item) => item === text);
};

const candidateStatusText = (candidate) => {
  if (candidate?.canQuote) {
    return candidate.source === 'seeded' ? 'confirmed place' : 'select to confirm';
  }
  return 'label only';
};

const candidateSecondaryText = (candidate) => {
  const primary = trim(candidate?.name || candidate?.label);
  const secondary = trim(candidate?.addressText || candidate?.publicAddress);
  if (!secondary || secondary === primary) return '';
  return secondary;
};

const formatDistanceMiles = (miles) => {
  const numeric = Number(miles);
  if (!Number.isFinite(numeric)) return '';
  if (numeric < 0.1) return 'under 0.1 mi';
  return `${numeric.toFixed(1)} mi`;
};

const quoteToPriceSuggestion = (quote) => {
  if (!quote) return null;
  const rate = Number(quote.pricingRule?.rate || 0);
  return {
    value: String(quote.suggestedPayUsd),
    status: 'quoted',
    label: `Estimated distance: ${formatDistanceMiles(
      quote.distanceMiles,
    )}. Suggested task pay: $${quote.suggestedPayUsd}.`,
    routeLabel: `${quote.originLabel || 'Pickup'} -> ${
      quote.destinationLabel || 'destination'
    }`,
    pricingRuleLabel: `Based on $${rate.toFixed(2)} / mile`,
  };
};

const isRoutePriceEligibleUseCase = (useCase) =>
  ['generic_assistance_task', 'restaurant_delivery', 'pickup_errand', 'ride_help'].includes(
    useCase,
  );

const FieldLabel = ({
  children,
  optional = false,
  tooltip,
}) => (
  <span className="localops-concierge__field-label">
    <span>{children}</span>
    <span className={optional ? 'localops-concierge__optional' : 'localops-concierge__required'}>
      {optional ? OPTIONAL_LABEL : 'Required'}
    </span>
    {tooltip ? (
      <span
        className="localops-concierge__tip"
        tabIndex="0"
        title={tooltip}
        aria-label="Field info"
      >
        ?
      </span>
    ) : null}
  </span>
);

const PlaceSuggestionOption = ({ place, onSelect }) => {
  const content = (
    <>
      <span className="localops-concierge__place-option-main">
        <strong>{place.name}</strong>
        {candidateSecondaryText(place) ? (
          <small>{candidateSecondaryText(place)}</small>
        ) : null}
      </span>
      <span className="localops-concierge__place-option-status">
        {candidateStatusText(place)}
      </span>
    </>
  );

  if (!place.canQuote) {
    return (
      <div className="localops-concierge__place-suggestion-item" role="note">
        {content}
      </div>
    );
  }

  return (
    <button type="button" onClick={() => onSelect(place)}>
      {content}
    </button>
  );
};

export default function LocalOpsConcierge() {
  const { player } = useContext(AuthContext);
  const allowed = canViewLocalOpsConcierge(player);
  const [form, setForm] = useState(buildDefaultConciergeForm);
  const [manualFields, setManualFields] = useState(() => new Set());
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copiedScript, setCopiedScript] = useState('');
  const [lastLinkSent, setLastLinkSent] = useState(false);
  const [confirmedPlaces, setConfirmedPlaces] = useState({
    pickup: null,
    destination: null,
  });
  const [placeResolution, setPlaceResolution] = useState({
    pickup: EMPTY_RESOLUTION,
    destination: EMPTY_RESOLUTION,
  });
  const [lastAutoSuggestedPay, setLastAutoSuggestedPay] = useState('');

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${player?.token || ''}`,
      },
    }),
    [player?.token],
  );

  const fetchDrafts = useCallback(async () => {
    if (!allowed) return;
    setLoadingDrafts(true);
    try {
      const res = await axiosConn.get(
        'local-ops/concierge/assistance-drafts?limit=25',
        authConfig,
      );
      setDrafts(normalizeRows(res));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load concierge drafts.',
      );
    } finally {
      setLoadingDrafts(false);
    }
  }, [allowed, authConfig]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const updateField = (
    field,
    value,
    { markManual = true, confirmedPlace } = {},
  ) => {
    const nextManual = new Set(manualFields);
    if (field === 'patternKey') {
      PATTERN_OWNED_MANUAL_FIELDS.forEach((manualField) =>
        nextManual.delete(manualField),
      );
    }
    if (markManual) nextManual.add(field);
    setManualFields(nextManual);
    if (field === 'patternKey') {
      setConfirmedPlaces({ pickup: null, destination: null });
      setPlaceResolution({ pickup: EMPTY_RESOLUTION, destination: EMPTY_RESOLUTION });
      setLastAutoSuggestedPay('');
    }
    if (field === 'pickupAddress' || field === 'deliveryAddress') {
      const key = field === 'pickupAddress' ? 'pickup' : 'destination';
      const seeded = getConfirmedSeededCandidate(value);
      setConfirmedPlaces((current) => ({
        ...current,
        [key]: confirmedPlace ||
          seeded ||
          (candidateMatchesText(current[key], value) ? current[key] : null),
      }));
      if (!confirmedPlace && !seeded) {
        setLastAutoSuggestedPay('');
      }
    }
    setForm((current) => {
      if (field === 'patternKey') {
        return applyPatternToForm(current, value, nextManual);
      }
      let next = {
        ...current,
        [field]: value,
        ...(field === 'source' ? { src: value } : {}),
      };
      if (field === 'useCase') {
        const preset = getUseCasePreset(value);
        next = {
          ...next,
          taskType: preset.taskType,
        };
        ['category', 'title', 'description', 'requesterFacingNotes'].forEach((presetField) => {
          if (!nextManual.has(presetField)) {
            next[presetField] = preset[presetField] || '';
          }
        });
      }
      if (['useCase', 'pickupAddress', 'deliveryAddress'].includes(field)) {
        const suggestion = getPriceSuggestion(next);
        if (!nextManual.has('suggestedPriceUsd')) {
          next.suggestedPriceUsd = suggestion.value || '';
        }
      }
      return next;
    });
    setError('');
    setNotice('');
    setLastLinkSent(false);
  };

  const applySuggestedPay = () => {
    const suggestion = priceSuggestion;
    if (!suggestion.value) return;
    setManualFields((current) => {
      const next = new Set(current);
      next.delete('suggestedPriceUsd');
      return next;
    });
    setLastAutoSuggestedPay(String(suggestion.value));
    setForm((current) => ({
      ...current,
      suggestedPriceUsd: String(suggestion.value),
    }));
  };

  const applyPlaceSuggestion = (field, place) => {
    const nextValue = candidateDisplayText(place);
    updateField(field, nextValue, {
      confirmedPlace: place.canQuote ? place : null,
    });
    const key = field === 'pickupAddress' ? 'pickup' : 'destination';
    setPlaceResolution((current) => ({
      ...current,
      [key]: {
        candidates: place.canQuote ? [place] : [],
        status: place.canQuote ? 'confirmed' : 'label_only',
        message: place.canQuote
          ? 'Place confirmed for route estimate.'
          : 'This local place is label-only. Confirm an address before route pricing.',
      },
    }));
  };

  useEffect(() => {
    if (!allowed) return undefined;
    const value = trim(form.pickupAddress);
    if (!value) {
      setPlaceResolution((current) => ({
        ...current,
        pickup: EMPTY_RESOLUTION,
      }));
      return undefined;
    }
    if (candidateMatchesText(confirmedPlaces.pickup, value)) {
      setPlaceResolution((current) => ({
        ...current,
        pickup: {
          candidates: [confirmedPlaces.pickup],
          status: 'confirmed',
          message: 'Place confirmed for route estimate.',
        },
      }));
      return undefined;
    }

    let active = true;
    setPlaceResolution((current) => ({
      ...current,
      pickup: { candidates: [], status: 'resolving', message: 'Resolving pickup...' },
    }));
    const timer = setTimeout(async () => {
      const candidates = await resolveConciergePlaceCandidates(value, {
        regionSlug: 'camden',
      });
      if (!active) return;
      setPlaceResolution((current) => ({
        ...current,
        pickup: {
          candidates,
          status: candidates.some((candidate) => candidate.canQuote)
            ? 'candidates'
            : candidates.length
              ? 'label_only'
              : 'unavailable',
          message: candidates.length
            ? candidates.some((candidate) => candidate.canQuote)
              ? 'Select a candidate to confirm route pricing.'
              : 'Known local label only. Type the business with city/ZIP or a full address to search.'
            : 'No confident place match. Enter a full address or set pay manually.',
        },
      }));
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [allowed, form.pickupAddress, confirmedPlaces.pickup]);

  useEffect(() => {
    if (!allowed) return undefined;
    const value = trim(form.deliveryAddress);
    if (!value) {
      setPlaceResolution((current) => ({
        ...current,
        destination: EMPTY_RESOLUTION,
      }));
      return undefined;
    }
    if (
      confirmedPlaces.destination &&
      candidateMatchesText(confirmedPlaces.destination, value)
    ) {
      setPlaceResolution((current) => ({
        ...current,
        destination: {
          candidates: [confirmedPlaces.destination],
          status: 'confirmed',
          message: 'Destination confirmed for route estimate.',
        },
      }));
      return undefined;
    }

    let active = true;
    setPlaceResolution((current) => ({
      ...current,
      destination: {
        candidates: [],
        status: 'resolving',
        message: 'Resolving destination...',
      },
    }));
    const timer = setTimeout(async () => {
      const candidates = await resolveConciergePlaceCandidates(value, {
        regionSlug: 'camden',
      });
      if (!active) return;
      setPlaceResolution((current) => ({
        ...current,
        destination: {
          candidates,
          status: candidates.some((candidate) => candidate.canQuote)
            ? 'candidates'
            : candidates.length
              ? 'label_only'
              : 'unavailable',
          message: candidates.length
            ? candidates.some((candidate) => candidate.canQuote)
              ? 'Select a candidate to confirm route pricing.'
              : 'Known local label only. Type the destination with city/ZIP or a full address to search.'
            : 'No confident destination match. Enter a full address or set pay manually.',
        },
      }));
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [allowed, form.deliveryAddress, confirmedPlaces.destination]);

  const copyOriginalRequestTo = (field) => {
    const sourceText = trim(form.originalRequest);
    if (!sourceText) return;
    updateField(field, sourceText);
  };

  const handleCopy = async (card) => {
    try {
      await navigator.clipboard?.writeText(card.text);
      setCopiedScript(card.title);
    } catch (err) {
      setCopiedScript('');
      setError('Copy failed. Select and copy the script manually.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!allowed) return;
    setSubmitting(true);
    setError('');
    setNotice('');
    try {
      const createRes = await axiosConn.post(
        'local-ops/concierge/assistance-drafts',
        buildDraftPayload(form),
        authConfig,
      );
      const created = createRes?.data?.data || {};
      const draftId = created.draftId;
      if (!draftId) throw new Error('Draft was created without an id.');

      const sendRes = await axiosConn.post(
        `local-ops/concierge/assistance-drafts/${draftId}/send-magic-link`,
        {},
        authConfig,
      );
      const sent = sendRes?.data?.data || {};
      setNotice(
        `Magic link sent. ${sent.fallbackReviewLink || created.fallbackReviewLink || ''}`.trim(),
      );
      setLastLinkSent(true);
      setForm(buildDefaultConciergeForm());
      setManualFields(new Set());
      setConfirmedPlaces({ pickup: null, destination: null });
      setPlaceResolution({ pickup: EMPTY_RESOLUTION, destination: EMPTY_RESOLUTION });
      setLastAutoSuggestedPay('');
      await fetchDrafts();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to create and send this draft.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const dynamicLabels = getDynamicLabels(form.useCase);
  const selectedPattern = CONCIERGE_PATTERNS.find(
    (pattern) => pattern.key === form.patternKey,
  );
  const effectivePricingRule =
    selectedPattern?.pricingRule?.type === 'per_mile'
      ? selectedPattern.pricingRule
      : isRoutePriceEligibleUseCase(form.useCase)
        ? DEFAULT_ROUTE_PRICING_RULE
        : selectedPattern?.pricingRule;
  const routeQuote = useMemo(
    () =>
      calculateConciergeRouteQuote({
        origin: confirmedPlaces.pickup,
        destination: confirmedPlaces.destination,
        pricingRule: effectivePricingRule,
      }),
    [confirmedPlaces.pickup, confirmedPlaces.destination, effectivePricingRule],
  );
  const basePriceSuggestion = getPriceSuggestion(form);
  const quotedPriceSuggestion = quoteToPriceSuggestion(routeQuote);
  const routeResolving =
    effectivePricingRule?.type === 'per_mile' &&
    (placeResolution.pickup.status === 'resolving' ||
      placeResolution.destination.status === 'resolving');
  const routeNeedsConfirmation =
    effectivePricingRule?.type === 'per_mile' &&
    trim(form.pickupAddress) &&
    trim(form.deliveryAddress) &&
    !routeQuote;
  const priceSuggestion = quotedPriceSuggestion ||
    (routeResolving
      ? {
          ...basePriceSuggestion,
          status: 'resolving',
          label: 'Resolving route...',
        }
      : routeNeedsConfirmation
        ? {
            ...basePriceSuggestion,
            status: 'needs_confirmation',
            label:
              'Confirm a pickup and destination candidate to estimate suggested pay, or enter pay manually.',
          }
        : basePriceSuggestion);
  const pickupSuggestions = placeResolution.pickup.candidates;
  const deliverySuggestions = placeResolution.destination.candidates;
  const canApplySuggestedPay =
    Boolean(priceSuggestion.value) &&
    String(form.suggestedPriceUsd || '') !== String(priceSuggestion.value);
  const needCaptured = Boolean(trim(form.originalRequest) || trim(form.description) || trim(form.title));
  const emailCaptured = Boolean(trim(form.requesterEmail));
  const draftClear = Boolean(trim(form.title) && trim(form.description));
  const priceSuggested = Number(form.suggestedPriceUsd) > 0;
  const relevantScriptTitles = getRelevantScriptTitles(form);

  useEffect(() => {
    if (!quotedPriceSuggestion?.value) return;
    const suggestion = String(quotedPriceSuggestion.value);
    const currentPay = String(form.suggestedPriceUsd || '').trim();
    const operatorEditedPay =
      manualFields.has('suggestedPriceUsd') && currentPay !== lastAutoSuggestedPay;
    if (operatorEditedPay) return;
    if (currentPay === suggestion) {
      if (lastAutoSuggestedPay !== suggestion) setLastAutoSuggestedPay(suggestion);
      return;
    }
    setLastAutoSuggestedPay(suggestion);
    setForm((current) => ({
      ...current,
      suggestedPriceUsd: suggestion,
    }));
  }, [
    quotedPriceSuggestion?.value,
    form.suggestedPriceUsd,
    manualFields,
    lastAutoSuggestedPay,
  ]);

  if (!allowed) {
    return (
      <main className="localops-concierge localops-concierge--locked" data-testid="localops-concierge-unauthorized">
        <section>
          <JGLocalOpsMark className="localops-concierge__brand" />
          <h1>Concierge unavailable</h1>
          <span className="localops-concierge__subtitle">
            LocalOps Concierge access is required.
          </span>
        </section>
      </main>
    );
  }

  return (
    <main className="localops-concierge" data-testid="localops-concierge-workbench">
      <header className="localops-concierge__header">
        <div>
          <JGLocalOpsMark className="localops-concierge__brand" />
          <h1>Camden Concierge</h1>
          <span className="localops-concierge__subtitle">
            Prefill local task drafts, send secure review links, and keep CS-ride separate.
          </span>
        </div>
        <div className="localops-concierge__hero-actions">
          <a href={LOCALOPS_OFFERING_CONCIERGE_ROUTE}>Offering LOC</a>
          <a href="/localops/ops">Ops Health</a>
        </div>
      </header>

      <section className="localops-concierge__grid">
        <form className="localops-concierge__panel" onSubmit={handleSubmit}>
          <div className="localops-concierge__section-title">
            <h2>Assistance Draft</h2>
            <span>{selectedPattern?.name || form.useCase}</span>
          </div>

          <div className="localops-concierge__checklist" data-testid="concierge-conversion-checklist">
            {[
              ['Need captured', needCaptured],
              ['Email captured', emailCaptured],
              ['Draft clear', draftClear],
              ['Price suggested', priceSuggested],
              ['Link sent', lastLinkSent],
              ['Awaiting claim/funding', lastLinkSent],
            ].map(([label, complete]) => (
              <span
                key={label}
                className={complete ? 'is-complete' : ''}
              >
                {complete ? '[x]' : '[ ]'} {label}
              </span>
            ))}
          </div>

          <section className="localops-concierge__pattern-picker" data-testid="concierge-pattern-selector">
            <div className="localops-concierge__minor-heading">
              <h3>Start from a pattern</h3>
              <span>deterministic v1</span>
            </div>
            <div className="localops-concierge__pattern-grid">
              {CONCIERGE_PATTERNS.map((pattern) => (
                <button
                  key={pattern.key}
                  type="button"
                  className={form.patternKey === pattern.key ? 'is-selected' : ''}
                  aria-pressed={form.patternKey === pattern.key}
                  onClick={() => updateField('patternKey', pattern.key, { markManual: false })}
                >
                  <strong>{pattern.name}</strong>
                  <span>{pattern.summary}</span>
                </button>
              ))}
            </div>
          </section>

          <label>
            <FieldLabel tooltip={TOOLTIP_COPY.requesterEmail}>
              Requester email
            </FieldLabel>
            <input
              type="email"
              value={form.requesterEmail}
              onChange={(event) => updateField('requesterEmail', event.target.value)}
              required
            />
          </label>

          <div className="localops-concierge__two">
            <label>
              <FieldLabel optional>Requester name</FieldLabel>
              <input
                value={form.requesterName}
                onChange={(event) => updateField('requesterName', event.target.value)}
              />
            </label>
            <label>
              <FieldLabel optional>Phone</FieldLabel>
              <input
                value={form.requesterPhone}
                onChange={(event) => updateField('requesterPhone', event.target.value)}
              />
            </label>
          </div>

          <div className="localops-concierge__two">
            <label>
              <FieldLabel tooltip={TOOLTIP_COPY.source}>
                Source
              </FieldLabel>
              <select
                data-testid="concierge-source-select"
                value={form.source}
                onChange={(event) => updateField('source', event.target.value)}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <FieldLabel tooltip={TOOLTIP_COPY.useCase}>
                Use case
              </FieldLabel>
              <select
                data-testid="concierge-use-case-select"
                value={form.useCase}
                onChange={(event) => updateField('useCase', event.target.value)}
              >
                {USE_CASE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label>
            <FieldLabel optional tooltip={TOOLTIP_COPY.sourceLabel}>
              Source label
            </FieldLabel>
            <input
              value={form.sourceLabel}
              onChange={(event) => updateField('sourceLabel', event.target.value)}
              placeholder="Hip Camden DM, Down Home call, phone call"
            />
          </label>

          <label>
            <FieldLabel optional tooltip={TOOLTIP_COPY.originalRequest}>
              Original request / source post
            </FieldLabel>
            <textarea
              data-testid="concierge-original-request"
              value={form.originalRequest}
              onChange={(event) => updateField('originalRequest', event.target.value)}
              placeholder="Paste the Facebook post, copied message, phone note, or restaurant/customer note here."
            />
          </label>
          <div className="localops-concierge__copy-actions">
            <button
              type="button"
              onClick={() => copyOriginalRequestTo('description')}
              disabled={!trim(form.originalRequest)}
            >
              Copy to description
            </button>
            <button
              type="button"
              onClick={() => copyOriginalRequestTo('internalNotes')}
              disabled={!trim(form.originalRequest)}
            >
              Copy to internal notes
            </button>
            <button
              type="button"
              onClick={() => copyOriginalRequestTo('requesterFacingNotes')}
              disabled={!trim(form.originalRequest)}
            >
              Copy to requester-facing notes
            </button>
          </div>

          <label>
            <FieldLabel>Title</FieldLabel>
            <input
              value={form.title}
              onChange={(event) => updateField('title', event.target.value)}
              required
            />
          </label>

          <label>
            <FieldLabel>Description</FieldLabel>
            <textarea
              value={form.description}
              onChange={(event) => updateField('description', event.target.value)}
              required
            />
          </label>

          <div className="localops-concierge__two">
            <label>
              <FieldLabel optional>{dynamicLabels.pickup}</FieldLabel>
              <input
                data-testid="concierge-pickup-input"
                value={form.pickupAddress}
                onChange={(event) => updateField('pickupAddress', event.target.value)}
              />
              {pickupSuggestions.length ? (
                <div className="localops-concierge__place-suggestions" data-testid="concierge-pickup-suggestions">
                  {pickupSuggestions.map((place) => (
                    <PlaceSuggestionOption
                      key={place.id}
                      place={place}
                      onSelect={(selectedPlace) =>
                        applyPlaceSuggestion('pickupAddress', selectedPlace)
                      }
                    />
                  ))}
                </div>
              ) : null}
              {placeResolution.pickup.message ? (
                <p className="localops-concierge__resolution-status">
                  {placeResolution.pickup.message}
                </p>
              ) : null}
            </label>
            <label>
              <FieldLabel optional>{dynamicLabels.destination}</FieldLabel>
              <input
                data-testid="concierge-destination-input"
                value={form.deliveryAddress}
                onChange={(event) => updateField('deliveryAddress', event.target.value)}
              />
              {deliverySuggestions.length ? (
                <div className="localops-concierge__place-suggestions" data-testid="concierge-delivery-suggestions">
                  {deliverySuggestions.map((place) => (
                    <PlaceSuggestionOption
                      key={place.id}
                      place={place}
                      onSelect={(selectedPlace) =>
                        applyPlaceSuggestion('deliveryAddress', selectedPlace)
                      }
                    />
                  ))}
                </div>
              ) : null}
              {placeResolution.destination.message ? (
                <p className="localops-concierge__resolution-status">
                  {placeResolution.destination.message}
                </p>
              ) : null}
            </label>
          </div>

          <div className="localops-concierge__two">
            <label>
              <FieldLabel optional>Category</FieldLabel>
              <input
                value={form.category}
                onChange={(event) => updateField('category', event.target.value)}
              />
            </label>
            <label>
              <FieldLabel tooltip={TOOLTIP_COPY.suggestedPriceUsd}>
                {dynamicLabels.pay}
              </FieldLabel>
              <input
                type="number"
                min="1"
                step="0.01"
                value={form.suggestedPriceUsd}
                onChange={(event) => updateField('suggestedPriceUsd', event.target.value)}
                required
              />
            </label>
          </div>
          <p className="localops-concierge__hint" data-testid="concierge-price-hint">
            {priceSuggestion.label}
          </p>
          <section className="localops-concierge__route-card" data-testid="concierge-route-pay-card">
            <div>
              <h3>Route / pay</h3>
              <span>{priceSuggestion.status.replace(/_/g, ' ')}</span>
            </div>
            <p>{priceSuggestion.routeLabel}</p>
            <p>{priceSuggestion.label}</p>
            <small>{priceSuggestion.pricingRuleLabel}</small>
            <small>
              Private addresses prepare the draft and are not shown publicly. The requester reviews before funding.
            </small>
            {canApplySuggestedPay ? (
              <button type="button" onClick={applySuggestedPay}>
                Apply suggested pay
              </button>
            ) : null}
          </section>

          <label>
            <FieldLabel optional tooltip={TOOLTIP_COPY.requesterFacingNotes}>
              Requester-facing notes
            </FieldLabel>
            <textarea
              value={form.requesterFacingNotes}
              onChange={(event) => updateField('requesterFacingNotes', event.target.value)}
            />
          </label>

          <label>
            <FieldLabel optional tooltip={TOOLTIP_COPY.internalNotes}>
              Internal notes
            </FieldLabel>
            <textarea
              value={form.internalNotes}
              onChange={(event) => updateField('internalNotes', event.target.value)}
              placeholder="Never sent to requester/helper/public surfaces."
            />
          </label>

          <section className="localops-concierge__preview" data-testid="concierge-task-preview">
            <div>
              <h3>What the requester will review</h3>
              <dl>
                <dt>Title</dt>
                <dd>{trim(form.title) || 'Not set yet'}</dd>
                <dt>Description</dt>
                <dd>{trim(form.description) || 'Not set yet'}</dd>
                <dt>{dynamicLabels.pickup}</dt>
                <dd>{trim(form.pickupAddress) || 'Optional / not set'}</dd>
                <dt>{dynamicLabels.destination}</dt>
                <dd>{trim(form.deliveryAddress) || 'Optional / not set'}</dd>
                <dt>{dynamicLabels.pay}</dt>
                <dd>{priceSuggested ? `$${Number(form.suggestedPriceUsd).toFixed(2)}` : 'Not set yet'}</dd>
                <dt>Route / pay</dt>
                <dd>{priceSuggestion.status === 'quoted' ? priceSuggestion.label : priceSuggestion.status.replace(/_/g, ' ')}</dd>
                <dt>Requester-facing notes</dt>
                <dd>{trim(form.requesterFacingNotes) || 'None'}</dd>
              </dl>
            </div>
            <div className="localops-concierge__preview-internal">
              <h3>Internal only</h3>
              <dl>
                <dt>Pattern</dt>
                <dd>{selectedPattern?.name || 'Custom assistance draft'}</dd>
                <dt>Source</dt>
                <dd>{form.source}</dd>
                <dt>Source label</dt>
                <dd>{trim(form.sourceLabel) || 'Optional / not set'}</dd>
                <dt>Route / pay status</dt>
                <dd>{priceSuggestion.status.replace(/_/g, ' ')}</dd>
                <dt>Internal notes</dt>
                <dd>{trim(form.internalNotes) || 'None'}</dd>
                <dt>Original request / source post</dt>
                <dd>{trim(form.originalRequest) || 'Not captured'}</dd>
              </dl>
            </div>
          </section>

          {error ? <div className="localops-concierge__error">{error}</div> : null}
          {notice ? <div className="localops-concierge__notice">{notice}</div> : null}

          <button type="submit" disabled={submitting}>
            {submitting ? 'Sending...' : 'Create draft & send magic link'}
          </button>
        </form>

        <section className="localops-concierge__panel" data-testid="concierge-assist-panel">
          <div className="localops-concierge__section-title">
            <h2>Concierge Assist</h2>
            <span>static scripts</span>
          </div>
          <p className="localops-concierge__core-line">
            I can send you a prefilled link. You can review it first, adjust anything,
            and only fund it if it looks right.
          </p>
          <div className="localops-concierge__scripts">
            {SCRIPT_CARDS.map((card) => (
              <article
                key={card.title}
                className={`localops-concierge__script-card ${
                  relevantScriptTitles.has(card.title) ? 'is-relevant' : ''
                }`.trim()}
                data-relevant={relevantScriptTitles.has(card.title) ? 'true' : 'false'}
              >
                <div>
                  <strong>
                    {card.title}
                    {relevantScriptTitles.has(card.title) ? (
                      <span className="localops-concierge__script-badge">Suggested</span>
                    ) : null}
                  </strong>
                  <button type="button" onClick={() => handleCopy(card)}>
                    {copiedScript === card.title ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </section>
      </section>

      <section className="localops-concierge__panel localops-concierge__drafts">
        <div className="localops-concierge__section-title">
          <h2>Recent Drafts</h2>
          <span>{loadingDrafts ? 'loading' : `${drafts.length} rows`}</span>
        </div>
        {drafts.length ? (
          <div className="localops-concierge__draft-list">
            {drafts.map((draft) => (
              <article key={draft.id || draft._id}>
                <strong>{draft.title}</strong>
                <span>{draft.status}</span>
                <p>
                  {draft.useCase || 'generic_assistance_task'} / {draft.sourceLabel || draft.source}
                </p>
                <small>{draft.customerEmail}</small>
              </article>
            ))}
          </div>
        ) : (
          <p>No generic concierge drafts yet.</p>
        )}
      </section>
    </main>
  );
}

export { SCRIPT_CARDS, USE_CASE_OPTIONS, SOURCE_OPTIONS, LOCALOPS_CONCIERGE_ROUTE };
